import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Play, ChevronRight, Quote, ExternalLink } from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Hero Banner Section
export const HeroBannerSection = ({ data }) => {
  if (!data?.title) return null;
  
  // Get position, scale and blur values with defaults
  const posX = data.image_position_x ?? 50;
  const posY = data.image_position_y ?? 50;
  const scale = data.image_scale ?? 100;
  const blur = data.image_blur ?? 0;
  
  return (
    <section 
      className="relative w-screen left-1/2 right-1/2 -mx-[50vw] min-h-[300px] md:min-h-[400px] flex items-center overflow-hidden"
      style={{ 
        backgroundColor: data.background_image ? undefined : '#18181b'
      }}
    >
      {/* Background Image with Position/Scale/Blur */}
      {data.background_image && (
        <div 
          className="absolute inset-0"
          style={{
            backgroundImage: `url(${data.background_image})`,
            backgroundPosition: `${posX}% ${posY}%`,
            backgroundSize: scale === 100 ? 'cover' : `${scale}%`,
            backgroundRepeat: 'no-repeat',
            filter: blur > 0 ? `blur(${blur}px)` : 'none',
            transform: blur > 0 ? 'scale(1.1)' : 'none' // Prevent blur edge artifacts
          }}
        />
      )}
      {/* Gradient Overlay */}
      {data.background_image && <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/50 to-transparent" />}
      
      <div className="relative max-w-[1400px] mx-auto px-4 md:px-8 py-12 w-full">
        <motion.h2 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          className="font-heading text-3xl md:text-4xl lg:text-5xl uppercase text-white mb-3"
        >
          {data.title}
        </motion.h2>
        {data.subtitle && (
          <p className="text-zinc-300 text-base md:text-lg max-w-2xl mb-6">{data.subtitle}</p>
        )}
        {data.cta_text && data.cta_link && (
          <Link 
            to={data.cta_link}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-orange-500 text-black font-bold uppercase text-sm hover:bg-orange-400 transition-colors"
          >
            {data.cta_text} <ChevronRight className="w-4 h-4" />
          </Link>
        )}
      </div>
    </section>
  );
};

// Text Block Section
export const TextBlockSection = ({ data }) => {
  if (!data?.content) return null;
  
  const alignment = {
    left: "text-left",
    center: "text-center mx-auto",
    right: "text-right ml-auto"
  };
  
  return (
    <section className="py-12 md:py-16 bg-zinc-900">
      <div className={`max-w-[1400px] mx-auto px-4 md:px-8 ${alignment[data.alignment] || ''}`}>
        {data.title && (
          <h2 className="font-heading text-3xl md:text-4xl uppercase text-white mb-6">{data.title}</h2>
        )}
        <div 
          className="text-zinc-300 prose prose-invert prose-lg max-w-3xl"
          dangerouslySetInnerHTML={{ __html: data.content.replace(/\n/g, '<br/>') }}
        />
      </div>
    </section>
  );
};

// Image Gallery Section
export const ImageGallerySection = ({ data }) => {
  const images = data?.images || [];
  if (images.length === 0) return null;
  
  return (
    <section className="py-12 md:py-16 bg-black">
      <div className="max-w-[1400px] mx-auto px-4 md:px-8">
        {data.title && (
          <h2 className="font-heading text-3xl md:text-4xl uppercase text-white mb-8">{data.title}</h2>
        )}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {images.map((img, idx) => (
            <motion.div 
              key={idx}
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ delay: idx * 0.05 }}
              className="aspect-square overflow-hidden group"
            >
              <img 
                src={img.url} 
                alt={img.caption || ''} 
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              />
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Video Embed Section
export const VideoEmbedSection = ({ data }) => {
  if (!data?.video_url) return null;
  
  const getVideoId = (url) => {
    const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/);
    return match ? match[1] : null;
  };
  
  const videoId = getVideoId(data.video_url);
  if (!videoId) return null;
  
  return (
    <section className="py-12 md:py-16 bg-zinc-900">
      <div className="max-w-[1000px] mx-auto px-4 md:px-8">
        {data.title && (
          <h2 className="font-heading text-3xl md:text-4xl uppercase text-white mb-6 text-center">{data.title}</h2>
        )}
        <div className="aspect-video bg-black">
          <iframe
            src={`https://www.youtube.com/embed/${videoId}`}
            className="w-full h-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            title={data.title || "Video"}
          />
        </div>
        {data.description && (
          <p className="text-zinc-400 mt-4 text-center">{data.description}</p>
        )}
      </div>
    </section>
  );
};

// Featured Video Hero Section
export const FeaturedVideoHeroSection = ({ data }) => {
  if (!data?.video_url) return null;
  
  const getVideoId = (url) => {
    const match = url?.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/);
    return match ? match[1] : null;
  };
  
  const videoId = getVideoId(data.video_url);
  const thumbnailUrl = videoId ? `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg` : null;
  
  return (
    <section className="relative min-h-[500px] md:min-h-[600px] flex items-end overflow-hidden" data-testid="featured-video-section">
      {/* Video Thumbnail Background */}
      {thumbnailUrl && (
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${thumbnailUrl})` }}
        />
      )}
      
      {/* Gradient Overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/70 to-transparent" />
      <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-transparent to-transparent" />
      
      {/* Content */}
      <div className="relative z-10 max-w-[1400px] mx-auto px-4 md:px-8 py-12 md:py-16 w-full">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="max-w-2xl"
        >
          {data.title && (
            <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl uppercase text-white mb-4 leading-tight">
              {data.title}
            </h2>
          )}
          {data.description && (
            <p className="text-zinc-300 text-lg md:text-xl mb-8 line-clamp-3">
              {data.description}
            </p>
          )}
          {data.button_text && (
            <Link
              to={data.button_link || data.video_url}
              target={data.video_url && !data.button_link ? "_blank" : undefined}
              rel={data.video_url && !data.button_link ? "noopener noreferrer" : undefined}
              className="inline-flex items-center gap-3 px-8 py-4 bg-orange-500 text-black font-bold uppercase text-sm tracking-wider hover:bg-orange-400 transition-colors group"
            >
              <Play className="w-5 h-5 fill-black" />
              {data.button_text}
              <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
          )}
        </motion.div>
      </div>
      
      {/* Play Button Overlay (centered) */}
      {videoId && (
        <a
          href={data.video_url}
          target="_blank"
          rel="noopener noreferrer"
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-20 h-20 md:w-24 md:h-24 bg-orange-500/90 hover:bg-orange-500 rounded-full flex items-center justify-center transition-all hover:scale-110 z-20"
        >
          <Play className="w-10 h-10 md:w-12 md:h-12 text-black fill-black ml-1" />
        </a>
      )}
    </section>
  );
};

// Stats Counter Section
export const StatsCounterSection = ({ data }) => {
  const stats = data?.stats || [];
  if (stats.length === 0) return null;
  
  return (
    <section className="py-12 md:py-16 bg-black border-y border-zinc-800">
      <div className="max-w-[1400px] mx-auto px-4 md:px-8">
        <div className={`grid grid-cols-2 md:grid-cols-${Math.min(stats.length, 4)} gap-8`}>
          {stats.map((stat, idx) => (
            <motion.div 
              key={idx}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.1 }}
              className="text-center"
            >
              <div className="font-heading text-4xl md:text-5xl lg:text-6xl text-orange-500 mb-2">
                {stat.value}
              </div>
              <div className="text-zinc-400 uppercase tracking-wider text-sm">{stat.label}</div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Team Showcase Section
export const TeamShowcaseSection = ({ data }) => {
  const teams = data?.teams || [];
  if (teams.length === 0) return null;
  
  return (
    <section className="py-12 md:py-16 bg-zinc-900">
      <div className="max-w-[1400px] mx-auto px-4 md:px-8">
        {data.title && (
          <h2 className="font-heading text-3xl md:text-4xl uppercase text-white mb-8">{data.title}</h2>
        )}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          {teams.map((team, idx) => (
            <div key={idx} className="bg-zinc-800 p-4 text-center">
              {team.logo_url && (
                <img src={team.logo_url} alt={team.name} className="w-16 h-16 mx-auto object-contain mb-2" />
              )}
              <div className="text-white font-bold text-sm">{team.name}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// CTA Banner Section
export const CTABannerSection = ({ data }) => {
  if (!data?.title) return null;
  
  return (
    <section 
      className="relative w-screen left-1/2 right-1/2 -mx-[50vw] py-16"
      style={{ 
        backgroundImage: data.background_image ? `url(${data.background_image})` : undefined,
        backgroundColor: data.background_color || '#f97316'
      }}
    >
      {data.background_image && <div className="absolute inset-0 bg-gradient-to-r from-black/80 to-black/40" />}
      <div className="relative max-w-[1400px] mx-auto px-4 md:px-8 text-center">
        <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl uppercase text-white mb-4">
          {data.title}
        </h2>
        {data.description && (
          <p className="text-white/80 text-lg max-w-2xl mx-auto mb-8">{data.description}</p>
        )}
        {data.button_text && data.button_link && (
          <Link 
            to={data.button_link}
            className="inline-flex items-center gap-2 px-8 py-4 bg-black text-white font-bold uppercase hover:bg-zinc-900 transition-colors"
          >
            {data.button_text}
          </Link>
        )}
      </div>
    </section>
  );
};

// Testimonials Section
export const TestimonialsSection = ({ data }) => {
  const testimonials = data?.testimonials || [];
  if (testimonials.length === 0) return null;
  
  return (
    <section className="py-12 md:py-16 bg-zinc-900">
      <div className="max-w-[1400px] mx-auto px-4 md:px-8">
        {data.title && (
          <h2 className="font-heading text-3xl md:text-4xl uppercase text-white mb-8 text-center">{data.title}</h2>
        )}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {testimonials.map((t, idx) => (
            <motion.div 
              key={idx}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.1 }}
              className="bg-black border border-zinc-800 p-6"
            >
              <Quote className="w-8 h-8 text-orange-500 mb-4" />
              <p className="text-zinc-300 italic mb-4">&ldquo;{t.quote}&rdquo;</p>
              <div className="text-white font-bold">{t.name}</div>
              {t.title && <div className="text-zinc-500 text-sm">{t.title}</div>}
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Sponsors Grid Section
export const SponsorsGridSection = ({ data }) => {
  const sponsors = data?.sponsors || [];
  
  const shouldTicker = sponsors.length > 5;
  
  if (sponsors.length === 0) return null;
  
  // Double the sponsors array for seamless infinite scroll
  const tickerSponsors = shouldTicker ? [...sponsors, ...sponsors] : sponsors;
  
  return (
    <section className="py-12 md:py-16 bg-black border-t border-zinc-800">
      <div className="max-w-[1400px] mx-auto px-4 md:px-8">
        {data.title && (
          <h2 className="font-heading text-2xl uppercase text-white mb-8 text-center">{data.title}</h2>
        )}
        
        {shouldTicker ? (
          // Ticker Mode (>5 sponsors) - Continuous scrolling marquee
          // Hover anywhere on the ticker to pause
          <div className="relative overflow-hidden group">
            <div 
              className="flex items-center gap-12 md:gap-16 sponsor-ticker-animation"
              style={{
                width: 'max-content',
              }}
            >
              {tickerSponsors.map((sponsor, idx) => (
                <a 
                  key={`ticker-${idx}`}
                  href={sponsor.link || '#'}
                  target={sponsor.link ? "_blank" : undefined}
                  rel="noopener noreferrer"
                  className="flex-shrink-0 grayscale hover:grayscale-0 transition-all duration-300 opacity-70 hover:opacity-100 transform hover:scale-110"
                  title={sponsor.name}
                >
                  {sponsor.logo_url ? (
                    <img 
                      src={sponsor.logo_url} 
                      alt={sponsor.name} 
                      className="h-20 md:h-28 lg:h-32 w-auto max-w-[220px] md:max-w-[280px] object-contain pointer-events-none" 
                    />
                  ) : (
                    <span className="text-white font-bold text-lg">{sponsor.name}</span>
                  )}
                </a>
              ))}
            </div>
            
            {/* CSS Animation for Ticker - Pauses on hover */}
            <style>{`
              @keyframes sponsorTickerScroll {
                0% { transform: translateX(0); }
                100% { transform: translateX(-50%); }
              }
              .sponsor-ticker-animation {
                animation: sponsorTickerScroll 30s linear infinite;
              }
              .group:hover .sponsor-ticker-animation {
                animation-play-state: paused;
              }
            `}</style>
          </div>
        ) : (
          // Grid Mode (≤5 sponsors)
          <div className="flex flex-wrap justify-center items-center gap-10 md:gap-14">
            {sponsors.map((sponsor, idx) => (
              <a 
                key={idx}
                href={sponsor.link || '#'}
                target={sponsor.link ? "_blank" : undefined}
                rel="noopener noreferrer"
                className="grayscale hover:grayscale-0 transition-all duration-300 opacity-70 hover:opacity-100 transform hover:scale-110"
                title={sponsor.name}
              >
                {sponsor.logo_url ? (
                  <img 
                    src={sponsor.logo_url} 
                    alt={sponsor.name} 
                    className="h-16 md:h-24 lg:h-28 w-auto max-w-[200px] md:max-w-[250px] object-contain" 
                  />
                ) : (
                  <span className="text-white font-bold text-lg">{sponsor.name}</span>
                )}
              </a>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

// Countdown Banner Section
export const CountdownBannerSection = ({ data }) => {
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, mins: 0, secs: 0 });
  
  useEffect(() => {
    if (!data?.event_date) return;
    
    const targetDate = new Date(`${data.event_date}T${data.event_time || '12:00'}:00`);
    if (isNaN(targetDate.getTime())) return;
    
    const updateCountdown = () => {
      const now = new Date().getTime();
      const diff = targetDate.getTime() - now;
      
      if (diff > 0) {
        setTimeLeft({
          days: Math.floor(diff / (1000 * 60 * 60 * 24)),
          hours: Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
          mins: Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60)),
          secs: Math.floor((diff % (1000 * 60)) / 1000)
        });
      } else {
        setTimeLeft({ days: 0, hours: 0, mins: 0, secs: 0 });
      }
    };
    
    updateCountdown();
    const timer = setInterval(updateCountdown, 1000);
    return () => clearInterval(timer);
  }, [data?.event_date, data?.event_time]);
  
  if (!data?.event_name) return null;
  
  return (
    <section 
      className="relative py-16 md:py-20"
      style={{ 
        backgroundImage: data.background_image ? `url(${data.background_image})` : undefined,
        backgroundColor: data.background_image ? undefined : '#18181b'
      }}
    >
      {data.background_image && <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/70 to-black/50" />}
      <div className="relative max-w-[1400px] mx-auto px-4 md:px-8 text-center">
        {data.title && (
          <div className="text-orange-500 text-sm uppercase tracking-widest mb-2">{data.title}</div>
        )}
        <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl uppercase text-white mb-8">{data.event_name}</h2>
        <div className="flex justify-center gap-4 md:gap-8 mb-8">
          {[
            { value: timeLeft.days, label: 'Days' },
            { value: timeLeft.hours, label: 'Hours' },
            { value: timeLeft.mins, label: 'Mins' },
            { value: timeLeft.secs, label: 'Secs' }
          ].map((item, idx) => (
            <div key={idx} className="bg-black/50 px-4 py-3 md:px-6 md:py-4 border border-white/10">
              <div className="font-heading text-3xl md:text-5xl text-orange-500">{String(item.value).padStart(2, '0')}</div>
              <div className="text-zinc-400 text-xs uppercase tracking-wider">{item.label}</div>
            </div>
          ))}
        </div>
        {data.cta_text && data.cta_link && (
          <Link 
            to={data.cta_link}
            className="inline-flex items-center gap-2 px-6 py-3 bg-orange-500 text-black font-bold uppercase hover:bg-orange-400 transition-colors"
          >
            {data.cta_text} <ChevronRight className="w-5 h-5" />
          </Link>
        )}
      </div>
    </section>
  );
};

// Social Feed Embed Section
export const SocialFeedSection = ({ data }) => {
  if (!data?.embed_code && !data?.handle) return null;
  
  const platformStyles = {
    twitter: { bg: 'bg-black', text: 'text-white', accent: '#1DA1F2' },
    facebook: { bg: 'bg-[#1877F2]', text: 'text-white', accent: '#1877F2' },
    instagram: { bg: 'bg-gradient-to-br from-purple-500 via-pink-500 to-orange-400', text: 'text-white', accent: '#E1306C' },
    tiktok: { bg: 'bg-black', text: 'text-white', accent: '#00F2EA' }
  };
  
  const style = platformStyles[data.platform] || platformStyles.twitter;
  
  return (
    <section className="py-12 md:py-16 bg-zinc-900">
      <div className="max-w-[800px] mx-auto px-4 md:px-8">
        {data.title && (
          <h2 className="font-heading text-3xl uppercase text-white mb-6 text-center">{data.title}</h2>
        )}
        {data.embed_code ? (
          <div 
            className="bg-black/50 p-4 rounded-lg"
            dangerouslySetInnerHTML={{ __html: data.embed_code }}
          />
        ) : (
          <div className="bg-black/50 p-8 text-center border border-white/10">
            <div className="text-zinc-400 mb-2">Follow us on {data.platform}</div>
            <a 
              href={`https://${data.platform}.com/${data.handle}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-orange-500 hover:text-orange-400 font-bold text-xl"
            >
              @{data.handle}
            </a>
          </div>
        )}
      </div>
    </section>
  );
};

// Video Playlist Section
export const VideoPlaylistSection = ({ data }) => {
  let videos = data?.videos || [];
  if (typeof videos === 'string') {
    videos = videos.split(',').map(url => ({ url: url.trim() }));
  }
  if (videos.length === 0) return null;
  
  const getVideoId = (url) => {
    const match = url?.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/);
    return match ? match[1] : null;
  };
  
  return (
    <section className="py-12 md:py-16 bg-black">
      <div className="max-w-[1400px] mx-auto px-4 md:px-8">
        {data.title && (
          <h2 className="font-heading text-3xl uppercase text-white mb-8">{data.title}</h2>
        )}
        <div className={`grid gap-4 ${data.layout === 'carousel' ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'}`}>
          {videos?.map((video, idx) => {
            const videoId = getVideoId(video.url);
            if (!videoId) return null;
            return (
              <div key={idx} className="bg-zinc-900">
                <div className="aspect-video">
                  <iframe
                    src={`https://www.youtube.com/embed/${videoId}`}
                    className="w-full h-full"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    title={video.title || `Video ${idx + 1}`}
                  />
                </div>
                {video.title && (
                  <div className="p-3">
                    <h4 className="text-white font-bold text-sm">{video.title}</h4>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// Recent Videos Section (Admin-managed with titles and dates)
export const RecentVideosSection = ({ data }) => {
  const videos = data?.videos || [];
  if (videos.length === 0) return null;
  
  const getVideoId = (url) => {
    const match = url?.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/);
    return match ? match[1] : null;
  };
  
  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };
  
  return (
    <section className="py-12 md:py-16 bg-black" data-testid="recent-videos-section">
      <div className="max-w-[1400px] mx-auto px-4 md:px-8">
        {data.title && (
          <div className="flex items-center justify-between mb-8">
            <h2 className="font-heading text-3xl uppercase text-white">{data.title}</h2>
            <Link 
              to="/watch" 
              className="text-orange-500 hover:text-orange-400 text-sm font-bold uppercase flex items-center gap-1"
            >
              View All <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {videos.map((video, idx) => {
            const videoId = getVideoId(video.url);
            if (!videoId) return null;
            
            return (
              <motion.a
                key={idx}
                href={video.url}
                target="_blank"
                rel="noopener noreferrer"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.1 }}
                className="group bg-zinc-900 border border-zinc-800 overflow-hidden hover:border-orange-500/50 transition-colors"
              >
                {/* Thumbnail with Play Overlay */}
                <div className="relative aspect-video overflow-hidden">
                  <img 
                    src={`https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`}
                    alt={video.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    onError={(e) => { e.target.src = `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`; }}
                  />
                  <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="w-16 h-16 bg-orange-500 rounded-full flex items-center justify-center">
                      <Play className="w-8 h-8 text-black fill-black ml-1" />
                    </div>
                  </div>
                </div>
                
                {/* Video Info */}
                <div className="p-4">
                  <h4 className="text-white font-bold text-base mb-2 line-clamp-2 group-hover:text-orange-500 transition-colors">
                    {video.title}
                  </h4>
                  {video.date && (
                    <p className="text-zinc-500 text-xs uppercase tracking-wider">
                      {formatDate(video.date)}
                    </p>
                  )}
                </div>
              </motion.a>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// Featured Content Cards Section
export const FeaturedContentCardsSection = ({ data }) => {
  const cards = data?.cards || [];
  if (cards.length === 0) return null;
  
  return (
    <section className="py-12 md:py-16 bg-zinc-900">
      <div className="max-w-[1400px] mx-auto px-4 md:px-8">
        {data.title && (
          <h2 className="font-heading text-3xl uppercase text-white mb-8">{data.title}</h2>
        )}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {cards.map((card, idx) => (
            <motion.div 
              key={idx}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.1 }}
              className="bg-black border border-zinc-800 overflow-hidden group"
            >
              {card.image_url && (
                <div className="aspect-video overflow-hidden">
                  <img 
                    src={card.image_url} 
                    alt={card.title} 
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
              )}
              <div className="p-6">
                <h3 className="text-white font-bold text-lg mb-2">{card.title}</h3>
                {card.description && <p className="text-zinc-400 text-sm mb-4">{card.description}</p>}
                {card.link && (
                  <Link to={card.link} className="text-orange-500 hover:text-orange-400 text-sm font-bold uppercase flex items-center gap-1">
                    Learn More <ChevronRight className="w-4 h-4" />
                  </Link>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Newsletter Signup Section
export const NewsletterSignupSection = ({ data }) => {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('');
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    
    try {
      await axios.post(`${API}/newsletter/subscribe`, { email });
      setStatus('success');
      setEmail('');
    } catch (err) {
      setStatus('error');
    }
  };
  
  return (
    <section className="py-12 md:py-16 bg-orange-500">
      <div className="max-w-[600px] mx-auto px-4 md:px-8 text-center">
        {data?.title && (
          <h2 className="font-heading text-3xl md:text-4xl uppercase text-black mb-4">{data.title}</h2>
        )}
        {data?.description && (
          <p className="text-black/80 mb-6">{data.description}</p>
        )}
        <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={data?.placeholder || "Enter your email"}
            className="flex-1 px-4 py-3 bg-black text-white placeholder-zinc-500 outline-none"
          />
          <button
            type="submit"
            className="px-6 py-3 bg-black text-white font-bold uppercase hover:bg-zinc-900 transition-colors"
          >
            {data?.button_text || 'Subscribe'}
          </button>
        </form>
        {status === 'success' && <p className="text-black mt-4 font-bold">Thanks for subscribing!</p>}
        {status === 'error' && <p className="text-red-800 mt-4">Something went wrong. Please try again.</p>}
      </div>
    </section>
  );
};

// Team Roster Section
export const TeamRosterSection = ({ data }) => {
  const members = data?.members || [];
  if (members.length === 0) return null;
  
  return (
    <section className="py-12 md:py-16 bg-zinc-900">
      <div className="max-w-[1400px] mx-auto px-4 md:px-8">
        {data.title && (
          <h2 className="font-heading text-3xl uppercase text-white mb-8">{data.title}</h2>
        )}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          {members.map((member, idx) => (
            <div key={idx} className="bg-black border border-zinc-800 p-4 text-center">
              {member.photo_url ? (
                <img src={member.photo_url} alt={member.name} className="w-20 h-20 mx-auto rounded-full object-cover mb-3" />
              ) : (
                <div className="w-20 h-20 mx-auto rounded-full bg-zinc-800 flex items-center justify-center mb-3">
                  <span className="text-2xl text-zinc-500">{member.name?.charAt(0)}</span>
                </div>
              )}
              <div className="text-white font-bold text-sm">{member.name}</div>
              {member.position && <div className="text-zinc-500 text-xs">{member.position}</div>}
              {member.number && <div className="text-orange-500 text-xs">#{member.number}</div>}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Quick Links Section
export const QuickLinksSection = ({ data }) => {
  const links = data?.links || [];
  if (links.length === 0) return null;
  
  return (
    <section className="py-12 md:py-16 bg-black">
      <div className="max-w-[1400px] mx-auto px-4 md:px-8">
        {data.title && (
          <h2 className="font-heading text-3xl uppercase text-white mb-8 text-center">{data.title}</h2>
        )}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          {links.map((link, idx) => (
            <Link
              key={idx}
              to={link.url || '#'}
              className="bg-zinc-900 border border-zinc-800 p-6 text-center hover:border-orange-500 transition-colors group"
            >
              {link.icon_url && (
                <img src={link.icon_url} alt={link.label} className="w-10 h-10 mx-auto mb-3 group-hover:scale-110 transition-transform" />
              )}
              <div className="text-white font-bold text-sm group-hover:text-orange-500 transition-colors">{link.label}</div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

// Master renderer for custom sections
export const CustomSectionRenderer = ({ section }) => {
  if (!section?.enabled) return null;
  
  const components = {
    hero_banner: HeroBannerSection,
    text_block: TextBlockSection,
    image_gallery: ImageGallerySection,
    video_embed: VideoEmbedSection,
    featured_video: FeaturedVideoHeroSection,
    video_playlist: VideoPlaylistSection,
    recent_videos: RecentVideosSection,
    stats_counter: StatsCounterSection,
    team_showcase: TeamShowcaseSection,
    cta_banner: CTABannerSection,
    testimonials: TestimonialsSection,
    sponsors_grid: SponsorsGridSection,
    countdown_banner: CountdownBannerSection,
    social_feed: SocialFeedSection,
    featured_content: FeaturedContentCardsSection,
    newsletter_signup: NewsletterSignupSection,
    team_roster: TeamRosterSection,
    quick_links: QuickLinksSection
  };
  
  const Component = components[section.template_id];
  if (!Component) {
    console.warn(`Unknown custom section template: ${section.template_id}`);
    return null;
  }
  
  return <Component data={section.data} />;
};

// Featured Videos Section (multiple videos)
export const FeaturedVideosSection = () => {
  const [videos, setVideos] = useState([]);
  
  useEffect(() => {
    const fetchVideos = async () => {
      try {
        const res = await axios.get(`${API}/settings/featured-videos`);
        setVideos(res.data || []);
      } catch (err) {
        console.error("Error fetching videos:", err);
      }
    };
    fetchVideos();
  }, []);
  
  if (videos.length === 0) return null;
  
  return (
    <section className="py-12 md:py-16 bg-zinc-900">
      <div className="max-w-[1400px] mx-auto px-4 md:px-8">
        <h2 className="font-heading text-3xl md:text-4xl uppercase text-white mb-8">Featured Videos</h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {videos.map((video) => (
            <a
              key={video.id}
              href={video.youtube_url}
              target="_blank"
              rel="noopener noreferrer"
              className="group block"
            >
              <div className="relative aspect-video bg-zinc-800 overflow-hidden">
                <img
                  src={video.thumbnail}
                  alt={video.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                />
                <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                  <Play className="w-12 h-12 text-white" />
                </div>
              </div>
              <h3 className="text-white text-sm mt-2 group-hover:text-orange-500 transition-colors line-clamp-2">
                {video.title}
              </h3>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
};

// CTA Banner from Settings
export const CTABannerFromSettings = () => {
  const [cta, setCta] = useState(null);
  
  useEffect(() => {
    const fetchCTA = async () => {
      try {
        const res = await axios.get(`${API}/settings/cta-banner`);
        setCta(res.data);
      } catch (err) {
        console.error("Error fetching CTA:", err);
      }
    };
    fetchCTA();
  }, []);
  
  if (!cta?.enabled || !cta?.title) return null;
  
  return (
    <section 
      className="relative w-screen left-1/2 right-1/2 -mx-[50vw] py-16"
      style={{ backgroundImage: cta.image_url ? `url(${cta.image_url})` : undefined }}
    >
      {cta.image_url && <div className="absolute inset-0 bg-gradient-to-r from-black/80 to-transparent" />}
      <div className="relative max-w-[1400px] mx-auto px-4 md:px-8">
        <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl uppercase text-white mb-4">
          {cta.title}
        </h2>
        {cta.subtitle && (
          <p className="text-white/80 text-lg max-w-2xl mb-8">{cta.subtitle}</p>
        )}
        {cta.button_text && cta.button_link && (
          <Link 
            to={cta.button_link}
            className="inline-flex items-center gap-2 px-8 py-4 bg-orange-500 text-black font-bold uppercase hover:bg-orange-400 transition-colors"
          >
            {cta.button_text}
          </Link>
        )}
      </div>
    </section>
  );
};

export default CustomSectionRenderer;
